import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { workspaceApi, formatApiError } from "../lib/api";
import { Loader2, ChevronLeft, Download } from "lucide-react";
import { toast } from "sonner";

/* Everything the old dashboard piled onto the front page lives here instead:
   reachable in one click from Home, absent when you don't want it. */

const RANGES = [7, 30, 90];
const TYPES = [
  { k: "all", l: "All" },
  { k: "lead", l: "Contacts" },
  { k: "conversation", l: "Conversations" },
  { k: "rating", l: "Ratings" },
];

const TYPE_TONE = {
  lead: "bg-b2-wash text-b2-washink",
  conversation: "bg-violet-50 text-violet-700",
  rating: "bg-b2-livewash text-b2-live",
};

function Panel({ title, description, children, action }) {
  return (
    <section className="b2-card p-6 mb-5">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-[16px] font-bold tracking-[-0.02em]">{title}</h2>
          {description && <p className="mt-0.5 text-[13px] text-b2-muted">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Nothing({ children }) {
  return <p className="text-[13.5px] text-b2-muted py-2">{children}</p>;
}

export default function Activity() {
  const [days, setDays] = useState(30);
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [digest, setDigest] = useState(null);
  const [byDay, setByDay] = useState([]);
  const [daily, setDaily] = useState([]);
  const [perf, setPerf] = useState([]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, type]);

  const load = async () => {
    setLoading(true);
    try {
      const [act, dig, bd, dl, pf] = await Promise.all([
        workspaceApi.activity(30, type === "all" ? undefined : type).catch(() => ({ data: { items: [] } })),
        workspaceApi.digest(1).catch(() => ({ data: null })),
        workspaceApi.conversationsByDay(14).catch(() => ({ data: null })),
        workspaceApi.ratingsDaily(days).catch(() => ({ data: { items: [] } })),
        workspaceApi.chatbotsPerformance(days).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(act?.data?.items || []);
      setDigest(dig?.data || null);
      setByDay(bd?.data?.series || []);
      setDaily(dl?.data?.items || []);
      setPerf(pf?.data?.items || []);
    } finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const res = await workspaceApi.activityExportCsv(days);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `activity-${days}d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    }
  };

  const rated = (perf || []).filter((p) => p.score !== null && p.score !== undefined).slice(0, 5);

  return (
    <div data-testid="activity-page">
      <Link to="/home" className="inline-flex items-center gap-1 text-[13px] text-b2-muted hover:text-b2-ink mb-5">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.9} /> Home
      </Link>

      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <h1 className="text-[26px] leading-tight font-bold tracking-[-0.02em]">Recent activity</h1>
          <p className="mt-1.5 text-[15px] text-b2-muted">What your assistants have been doing.</p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((d) => (
            <button
              key={d} type="button" onClick={() => setDays(d)}
              data-testid={`range-${d}`}
              className={`h-9 px-3.5 rounded-[10px] text-[13px] font-medium border transition-colors ${
                days === d
                  ? "bg-b2-wash text-b2-washink border-transparent"
                  : "bg-white text-b2-muted border-b2-line hover:text-b2-ink"
              }`}
            >{d} days</button>
          ))}
          <button
            type="button" onClick={handleExport} data-testid="export-activity"
            className="h-9 px-3.5 rounded-[10px] text-[13px] font-medium bg-white text-b2-muted border border-b2-line hover:text-b2-ink inline-flex items-center gap-1.5"
          >
            <Download className="w-4 h-4" strokeWidth={1.8} /> Export
          </button>
        </div>
      </div>

      {loading && (
        <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-b2-faint" /></div>
      )}

      {!loading && (
        <>
          {digest && (
            <Panel title="Last 24 hours">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
                {[
                  ["Conversations", digest.conversations || 0],
                  ["Messages", digest.messages || 0],
                  ["Contacts", digest.leads || 0],
                  ["Satisfaction", digest.satisfaction != null ? `${digest.satisfaction}%` : "—"],
                ].map(([l, v]) => (
                  <div key={l}>
                    <div className="text-[24px] font-bold tabular-nums">{v}</div>
                    <div className="text-[13px] text-b2-muted">{l}</div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel
            title="Activity"
            description="Newest first."
            action={
              <div className="flex items-center gap-1.5">
                {TYPES.map((o) => (
                  <button
                    key={o.k} type="button" onClick={() => setType(o.k)}
                    data-testid={`act-type-${o.k}`}
                    className={`h-8 px-3 rounded-full text-[12.5px] font-medium border transition-colors ${
                      type === o.k
                        ? "bg-b2-wash text-b2-washink border-transparent"
                        : "bg-white text-b2-muted border-b2-line hover:text-b2-ink"
                    }`}
                  >{o.l}</button>
                ))}
              </div>
            }
          >
            {items.length === 0 ? (
              <Nothing>Nothing in this period yet. Visitor activity will appear here.</Nothing>
            ) : (
              <ul className="divide-y divide-b2-line -mx-1">
                {items.map((it, i) => (
                  <li key={`${it.type}-${it.id || i}`} className="py-2.5 px-1 flex items-center gap-3 text-[13.5px]">
                    <span className={`px-2 py-0.5 rounded-full text-[11.5px] font-medium shrink-0 ${TYPE_TONE[it.type] || "bg-black/5 text-b2-muted"}`}>
                      {it.type}
                    </span>
                    <span className="font-medium truncate">{it.label}</span>
                    <span className="text-b2-muted truncate">{it.meta}</span>
                    <span className="ml-auto text-[12px] text-b2-faint shrink-0">
                      {it.at ? new Date(it.at).toLocaleString() : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {byDay.length > 0 && (
            <Panel title="Conversations by day" description="Last 14 days.">
              <div className="flex items-end gap-1 h-24">
                {(() => {
                  const max = byDay.reduce((m, r) => Math.max(m, r.count || 0), 0) || 1;
                  return byDay.map((r, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5" title={`${r.date}: ${r.count}`}>
                      <div
                        className="w-full rounded-t-[3px] bg-b2-indigo/85"
                        style={{ height: `${Math.round(((r.count || 0) / max) * 100)}%`, minHeight: r.count ? "2px" : "0" }}
                      />
                      <div className="text-[10px] text-b2-faint">{(r.date || "").slice(5)}</div>
                    </div>
                  ));
                })()}
              </div>
            </Panel>
          )}

          {daily.length > 0 && (
            <Panel title="Satisfaction" description={`Thumbs up and down over ${days} days.`}>
              {(() => {
                const maxV = Math.max(1, ...daily.map((d) => (d.up || 0) + (d.down || 0)));
                return (
                  <>
                    <div className="flex items-end gap-0.5 h-20">
                      {daily.map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col justify-end gap-px min-w-0"
                             title={`${d.date || ""}: ${d.up || 0} up / ${d.down || 0} down`}>
                          <div className="bg-b2-live rounded-sm" style={{ height: `${((d.up || 0) / maxV) * 100}%` }} />
                          <div className="bg-rose-400 rounded-sm" style={{ height: `${((d.down || 0) / maxV) * 100}%` }} />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center text-[12px] text-b2-faint mt-2.5">
                      <span>{daily[0]?.date || ""}</span>
                      <span className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-b2-live inline-block" /> Up</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-400 inline-block" /> Down</span>
                      </span>
                      <span>{daily[daily.length - 1]?.date || ""}</span>
                    </div>
                  </>
                );
              })()}
            </Panel>
          )}

          {rated.length > 0 && (
            <Panel title="Assistant breakdown" description={`Ranked by satisfaction over ${days} days.`}>
              <ul className="divide-y divide-b2-line -mx-1">
                {rated.map((p, i) => (
                  <li key={p.chatbot_id} className="py-3 px-1 flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-black/[0.04] text-b2-muted flex items-center justify-center text-[12px] font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium truncate">{p.name}</div>
                      <div className="text-[12.5px] text-b2-muted">
                        {p.up} up · {p.down} down · {p.conversations} conversation{p.conversations === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="text-[15px] font-semibold tabular-nums shrink-0">{p.score}%</div>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
